import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Play, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { apiCall } from "../App";

const DemoVideoModal = ({ show, onClose, placement = "login" }) => {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setLoading(true);
      apiCall("GET", `/system/demo-video/${placement}`).then(res => {
        if (res.success) {
          const v = res.data.video;
          setVideo(v);
          // Record a play for analytics (fire-and-forget)
          if (v) {
            apiCall("POST", `/system/demo-videos/play/${placement}`).catch(() => {});
          }
        }
        setLoading(false);
      });
    }
  }, [show, placement]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [show, handleKeyDown]);

  if (!show) return null;

  const renderVideo = () => {
    if (loading) {
      return (
        <div className="text-center space-y-3 py-8">
          <div className="w-8 h-8 mx-auto border-2 border-divine-gold/30 border-t-divine-gold rounded-full animate-spin" />
          <p className="text-chakra-crown/40 text-sm">Loading video...</p>
        </div>
      );
    }

    if (!video || !video.embed_url) {
      return (
        <div className="text-center space-y-3 py-8">
          <div className="w-16 h-16 mx-auto rounded-full border-2 border-divine-gold/30 flex items-center justify-center">
            <Play className="w-8 h-8 text-divine-gold/50" />
          </div>
          <p className="text-divine-gold/60 text-sm">Demo video coming soon</p>
          <p className="text-chakra-crown/40 text-xs max-w-sm mx-auto">
            A walkthrough of your 3A Transformation Journey — Awareness, Alignment, Awakening
          </p>
        </div>
      );
    }

    if (video.video_type === "direct") {
      return (
        <video
          src={video.embed_url}
          poster={video.thumbnail_url || undefined}
          controls
          autoPlay
          className="w-full rounded-xl"
          data-testid="demo-video-player"
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    // YouTube / Vimeo / other embed
    return (
      <iframe
        src={video.embed_url + "?autoplay=1&rel=0"}
        title={video.title || "Demo Video"}
        className="w-full aspect-video rounded-xl"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        data-testid="demo-video-iframe"
      />
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
      data-testid="demo-video-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-3xl mx-4">
        <div className="rounded-2xl border border-divine-gold/20 bg-cosmic-void overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-5 py-3 border-b border-divine-gold/10">
            <h3 className="font-heading text-base text-divine-gold">{video?.title || "SIWON HARMONY Demo"}</h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-chakra-crown/10 hover:bg-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold transition-colors"
              data-testid="close-demo-video"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4" data-testid="demo-video-content">
            {renderVideo()}
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

const DemoVideoButton = ({ className = "", placement = "login", label = "Watch Demo" }) => {
  const [show, setShow] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/system/demo-video/${placement}`).then(res => {
      if (cancelled) return;
      const v = res?.success ? res.data?.video : null;
      if (v?.thumbnail_url) {
        const url = v.thumbnail_url.startsWith("/api")
          ? `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}`
          : v.thumbnail_url;
        setThumbnail(url);
      }
    });
    return () => { cancelled = true; };
  }, [placement]);

  if (thumbnail) {
    return (
      <>
        <button
          onClick={() => setShow(true)}
          className={`group relative inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-divine-gold/30 bg-cosmic-void/40 hover:bg-divine-gold/10 transition-all ${className}`}
          data-testid={`watch-demo-btn-${placement}`}
        >
          <span className="relative block w-8 h-8 rounded-full overflow-hidden border border-divine-gold/30">
            <img src={thumbnail} alt="" className="w-full h-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors">
              <Play className="w-3.5 h-3.5 text-divine-gold fill-divine-gold drop-shadow" />
            </span>
          </span>
          <span className="text-sm font-medium text-divine-gold">{label}</span>
        </button>
        <DemoVideoModal show={show} onClose={() => setShow(false)} placement={placement} />
      </>
    );
  }

  return (
    <>
      <Button variant="outline" onClick={() => setShow(true)}
        className={`border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10 ${className}`}
        data-testid={`watch-demo-btn-${placement}`}>
        <Play className="w-4 h-4 mr-2" />{label}
      </Button>
      <DemoVideoModal show={show} onClose={() => setShow(false)} placement={placement} />
    </>
  );
};

export { DemoVideoButton, DemoVideoModal };
export default DemoVideoButton;
